/**
 * Hook that emits a named `screen_*` event on entry to each in-studio screen
 * (ENG-4324, plan SCR-1).
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import { createScreenLifecycleTracker } from '#~/analytics/domains/screen-lifecycle.js';
import { resolveScreenForPath } from './resolve-screen.js';

/**
 * The screen this DOCUMENT last reported, or null before the first one.
 *
 * Deliberately module scope, not a `useRef` — the same choice, for the same
 * reason, as `lastRouteKey` in pageview/use-pageview-tracker.ts. This hook
 * renders inside `<Trackers>`, which sits inside the auth `Synchronizer`; that
 * renders `<Loader/>` instead of the app subtree while the studio user resolves,
 * then swaps the subtree back once it has, so React unmounts and remounts this
 * hook on a route the user never left. A per-instance ref starts empty on the
 * remount and reports the same screen a second time — measured at 28% of real
 * `screen_room` sends. Document scope survives any remount while still letting a
 * genuine re-entry count again.
 *
 * Keyed on the resolved screen IDENTITY (name + room) rather than the raw
 * pathname, because the identity is what the emitted event is made of. Two rooms
 * are two screens even though both send `screen_room`, and two pathnames can be
 * one screen entry: `/billing/:mode` and `/billing/:mode/:status` both resolve to
 * `screen_room_billing` for the same room, so the provider appending a status
 * segment is a progression WITHIN the billing screen rather than a second entry
 * to it. Keying on the pathname there would emit a duplicate that carries
 * identical name and room and so cannot be told apart downstream.
 *
 * null is also the key for every non-screen route, which is what keeps leaving
 * the studio and returning a fresh entry: the key drops to null on the way out,
 * so the room no longer matches on the way back in.
 */
let lastScreenKey: string | null = null;

export function useScreenTracker(): void {
  const location = useLocation();
  const { track } = useAnalytics();

  useEffect(() => {
    const resolved = resolveScreenForPath(location.pathname);
    // Unambiguous: no `screen_*` name contains a colon, so no two distinct
    // (name, room) pairs can collide on one key.
    const screenKey = resolved === null ? null : `${resolved.name}:${resolved.roomId}`;

    if (screenKey === lastScreenKey) {
      return;
    }
    // Advance for non-screen routes too (the key becomes null) so a return fires.
    lastScreenKey = screenKey;

    if (resolved === null) {
      return;
    }

    createScreenLifecycleTracker(track).trackScreenViewed({
      screen: resolved.name,
      room_id: resolved.roomId
    });
  }, [location.pathname, track]);
}

/**
 * Reset the document-scoped screen dedup.
 *
 * Only a test needs this: the key intentionally lives as long as the document,
 * so without a reset one case's emit would suppress the next case's.
 */
export function resetScreenDedupForTests(): void {
  lastScreenKey = null;
}
